/**
 * "กำลังจัดส่ง" in the customer panel: GET /api/v1/pos/conversations/:id/deliveries (api/app/pos_deliveries.py).
 * The server picks the customer (linked POS customer · web login phone — never the phone typed into the chat) and
 * decides each order's `phase`; the POS
 * part is chat_v1 `deliveries` (orderbeta service/chat_v1/deliveries.php). Mock mode serves the demo orders below.
 */
import { ApiError } from './client'
import { reportUnauthorized } from './http'
import { useMock } from './index'

export type DeliveryCarrier = 'lalamove' | 'dhl' | 'other'
export type DeliveryPhase =
  | 'scheduled'
  | 'preparing'
  | 'finding_driver'
  | 'to_pickup'
  | 'on_the_way'
  | 'label_created'
  | 'picked_up'
  | 'in_transit'
  | 'out_for_delivery'
  | 'delivered'
  | 'failed'
  | 'returned'
  | 'carrier_cancelled'

export interface DeliveryLalamove {
  status: string | null
  /** stop = the customer's own stop is known (queue, delivered time) · trip = only the trip's status */
  matched: 'stop' | 'trip'
  /** Staff only: to call the driver. `photo` = the carrier's https picture (fallback icon when missing / broken). */
  driver: {
    name: string | null
    plate: string | null
    phone: string | null
    phone_display: string | null
    photo?: string | null
  } | null
  stops_ahead: number | null
  stops_total: number | null
  /** Customer stops of this trip already delivered. */
  stops_done?: number | null
  picked_up_at: string | null
  delivered_at: string | null
  location: { at: string; age_min: number; fresh: boolean } | null
  eta: { minutes: number; arrival: string | null } | null
  /** On the way and the driver has not really moved for this many minutes (≥ 10, fresh position only). */
  still_min?: number | null
  /** Straight line from the driver's last position to the customer's pin (fresh position only). */
  distance_km?: number | null
  /** Lalamove's live map of the trip ("แผนที่สด"). */
  share_link?: string | null
  /** Proof of delivery photo (delivered only) — the carrier's https URL, staff panel only. */
  pod_image?: string | null
}

export interface DeliveryScan {
  code: string | null
  /** What the code means (PICKED_UP, IN_TRANSIT, OUT_FOR_DELIVERY, DELIVERED …) — null = unknown code. */
  key: string | null
  text: string | null
  place: string | null
  at: string | null
}

/** The delivery round against the arrival (ETA while on the way) or the delivered time — decided by the API. */
export interface DeliveryTiming {
  slot_end: string
  /** 0 = in time. */
  late_min: number
  basis: 'eta' | 'delivered'
}

export interface DeliveryDhl {
  tracking_no: string | null
  status_key: string | null
  status_code: string | null
  status_text: string | null
  event_at: string | null
  event_text: string | null
  place: string | null
  checked_at: string | null
  label_at: string | null
  track_url: string | null
  /** Scan history, newest first (≤ 20). */
  scans?: DeliveryScan[]
  weight_g?: number | null
  /** No new scan for this many hours (≥ 24, parcel still moving). */
  quiet_h?: number | null
  /** Proof of delivery (delivered only): receiver, signature (small data: image), photo (https). */
  epod_name?: string | null
  epod_signature?: string | null
  delivery_image?: string | null
}

export interface DeliveryOrder {
  id: string
  code: string | null
  carrier: DeliveryCarrier
  phase: DeliveryPhase
  delivery_date: string | null
  slot: string | null
  round: { no: number; of: number } | null
  pack: string | null
  meals: number
  snacks: number
  total: number | null
  payment: { method: string | null; cod: boolean } | null
  pos_status: string | null
  pos_status_text: string | null
  /** Last word from the carrier (trip update / DHL scan). */
  updated_at: string | null
  delivered_at: string | null
  /** Delivered (≤ 48 h ago): shown last, dimmed. */
  dim: boolean
  /** The customer's own bill page (live status) — safe to paste to this customer. */
  bill_url: string | null
  /** Lalamove: arrival / delivered time vs the delivery round (null = no round or nothing to compare). */
  timing?: DeliveryTiming | null
  /** A time under each of the 4 steps — only times the carriers gave (null = unknown, shown blank). */
  steps_at?: (string | null)[] | null
  lalamove: DeliveryLalamove | null
  dhl: DeliveryDhl | null
}

/**
 * A held bill the chat saved "ยังไม่โอน · ส่ง QR ให้ลูกค้า" (api/app/pos_pending.py) still waiting for the transfer —
 * "รอชำระ" in the panel. `late` = past the website's deadline (21:30 the day before delivery); nothing happens by itself.
 */
export interface DeliveryUnpaid {
  id: string
  /** hold = a POS held bill (QR transfer) · card = a card payment link (api/app/pos_card.py) · absent on an older API = hold */
  kind?: 'hold' | 'card'
  code: string | null
  amount: number | null
  pay_due_at: string | null
  late: boolean
  delivery_date: string | null
  pack: string | null
  meals: number
}

export interface Deliveries {
  conversation_id?: string
  /** no_customer = not linked / no phone (the section hides) · pending = the POS has not answered yet */
  state: 'ok' | 'no_customer' | 'pending' | 'pos_error'
  orders: DeliveryOrder[]
  more: number
  today?: string
  partial?: boolean
  fetched_at?: string
  /** The answer is older than the cache window (the POS is slow or down right now). */
  stale?: boolean
  message?: string | null
  /** Held orders waiting for the transfer (newest first, ≤ 5). Absent on an older API. */
  unpaid?: DeliveryUnpaid[]
}

export interface DeliveriesApi {
  get(conversationId: string, fresh?: boolean): Promise<Deliveries>
}

export class HttpDeliveriesApi implements DeliveriesApi {
  constructor(
    private readonly base: string,
    private readonly timeoutMs = 12000,
    private readonly fetchImpl: typeof fetch = (...a) => fetch(...a),
  ) {}

  async get(conversationId: string, fresh = false): Promise<Deliveries> {
    const u = new URL(
      `${this.base.replace(/\/+$/, '')}/api/v1/pos/conversations/${encodeURIComponent(conversationId)}/deliveries`,
      window.location.origin,
    )
    if (fresh) u.searchParams.set('fresh', '1')
    const ctrl = new AbortController()
    const timer = setTimeout(() => ctrl.abort(), this.timeoutMs)
    try {
      const res = await this.fetchImpl(u.toString(), {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
        signal: ctrl.signal,
      })
      if (res.status === 401) reportUnauthorized()
      if (!res.ok) {
        let message = `HTTP ${res.status}`
        try {
          const j = (await res.json()) as { detail?: unknown }
          if (typeof j.detail === 'string') message = j.detail
        } catch {
          /* body not JSON */
        }
        throw new ApiError(res.status, message)
      }
      return (await res.json()) as Deliveries
    } catch (e) {
      if (e instanceof ApiError) throw e
      throw new ApiError(0, 'ต่อเซิร์ฟเวอร์ไม่ได้ ลองใหม่อีกครั้ง')
    } finally {
      clearTimeout(timer)
    }
  }
}

// ---------------------------------------------------------------- mock (demo data, times relative to now)

const iso = (msAgo: number): string => new Date(Date.now() - msAgo).toISOString()
const MIN = 60_000
/** YYYY-MM-DD in Bangkok, `days` from today. */
function bkkDay(days: number): string {
  return new Date(Date.now() + 7 * 3600_000 + days * 86400_000).toISOString().slice(0, 10)
}
function bkkClock(msAhead: number): string {
  return new Date(Date.now() + 7 * 3600_000 + msAhead).toISOString().slice(11, 16)
}

const BASE: Omit<DeliveryOrder, 'id' | 'code' | 'carrier' | 'phase'> = {
  delivery_date: null,
  slot: null,
  round: null,
  pack: 'Pro 7 day 21 มื้อ ลด 5%',
  meals: 21,
  snacks: 0,
  total: 1265,
  payment: { method: 'BankTransfer', cod: false },
  pos_status: '3',
  pos_status_text: 'กำลังจัดส่ง',
  updated_at: null,
  delivered_at: null,
  dim: false,
  bill_url: 'https://cleanfit.cloud/beta/buy/order/demo',
  lalamove: null,
  dhl: null,
}

/** Small abstract SVG "photos" (no network, no real people) — like mockAvatars.ts. */
function svg(w: number, h: number, body: string): string {
  return (
    'data:image/svg+xml;utf8,' +
    encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}">${body}</svg>`)
  )
}
const DRIVER_A = svg(96, 96, '<rect width="96" height="96" fill="#cbb8a3"/><circle cx="48" cy="38" r="17" fill="#f3e6d8"/><ellipse cx="48" cy="92" rx="34" ry="26" fill="#f16622"/>')
const DRIVER_B = svg(96, 96, '<rect width="96" height="96" fill="#b9c7d6"/><circle cx="48" cy="38" r="17" fill="#efe2d2"/><ellipse cx="48" cy="92" rx="34" ry="26" fill="#2f5d8a"/>')
/** A parcel at a door. */
const POD_LALA = svg(320, 240, '<rect width="320" height="240" fill="#d9d2c7"/><rect x="40" y="0" width="120" height="200" fill="#8e735c"/><circle cx="140" cy="110" r="6" fill="#e8d9b0"/><rect y="200" width="320" height="40" fill="#b7ad9f"/><rect x="180" y="150" width="90" height="62" rx="6" fill="#f3f0ea"/><rect x="196" y="140" width="58" height="22" rx="6" fill="#0e7a5c"/>')
const POD_DHL = svg(320, 240, '<rect width="320" height="240" fill="#e6e0d6"/><rect x="0" y="170" width="320" height="70" fill="#c9bfae"/><rect x="100" y="95" width="120" height="90" fill="#c9a36b"/><rect x="100" y="95" width="120" height="16" fill="#ffcc00"/><rect x="140" y="125" width="40" height="10" fill="#d40511"/>')
const SIGNATURE = svg(140, 60, '<rect width="140" height="60" fill="#ffffff"/><path d="M10 42 C 25 10, 35 55, 50 30 S 70 12, 80 40 S 105 50, 130 18" fill="none" stroke="#1f3b8a" stroke-width="3" stroke-linecap="round"/>')

/** A Bangkok round like the POS labels it ("11.00-13.00 น.") around a time `msAhead` from now. */
function roundAround(msAhead: number): { label: string; end: string } {
  const h = new Date(Date.now() + 7 * 3600_000 + msAhead).getUTCHours()
  const start = Math.max(0, Math.min(21, h - 1))
  const pad = (n: number) => String(n).padStart(2, '0')
  return { label: `${pad(start)}.00-${pad(start + 2)}.00 น.`, end: `${pad(start + 2)}:00` }
}
/** A round (whole hours, like the POS) that the ETA misses by exactly `lateMin`: the ETA is 10–69 min from now. */
function roundMissedBy(lateMin: number): { label: string; end: string; etaMin: number } {
  const nowB = Date.now() + 7 * 3600_000
  let endMs = Math.floor(nowB / 3600_000) * 3600_000
  if (endMs + lateMin * 60_000 - nowB < 10 * 60_000) endMs += 3600_000
  const endH = new Date(endMs).getUTCHours()
  const pad = (n: number) => String(n).padStart(2, '0')
  return {
    label: `${pad((endH + 22) % 24)}.00-${pad(endH)}.00 น.`,
    end: `${pad(endH)}:00`,
    etaMin: Math.round((endMs + lateMin * 60_000 - nowB) / 60_000),
  }
}
const scanAt = (code: string, key: string | null, text: string, place: string | null, msAgo: number): DeliveryScan => ({
  code,
  key,
  text,
  place,
  at: iso(msAgo),
})
const HOUR = 3600_000

/**
 * c102 = the six states of the owner-approved mockup (v4, 28 ก.ย.), in the order the API sorts them (delivery date,
 * then what needs staff; delivered last, dimmed): DHL stuck · DHL in transit · Lalamove on the way in time ·
 * Lalamove late with a driver not moving · DHL delivered · Lalamove delivered with a photo.
 * c104 = the compact cards (driver going to the shop · DHL label waiting for pickup · upcoming round).
 */
export function mockDeliveries(conversationId: string): Deliveries {
  const now = Date.now()
  const today = bkkDay(0)
  if (conversationId === 'c102') {
    const onTime = roundAround(18 * MIN)
    const late = roundMissedBy(59)
    const doneRound = roundAround(-38 * MIN)
    const orders: DeliveryOrder[] = [
      {
        ...BASE,
        id: '292211',
        code: '20260924-017',
        carrier: 'dhl',
        phase: 'in_transit',
        delivery_date: bkkDay(-2),
        pack: 'Pro 7 day 21 มื้อ',
        updated_at: iso(31 * HOUR),
        steps_at: [iso(44 * HOUR), iso(40 * HOUR), null, null],
        dhl: {
          tracking_no: '7127099912870601',
          status_key: 'IN_TRANSIT',
          status_code: '77169',
          status_text: 'พัสดุออกจากศูนย์คัดแยก',
          event_at: iso(31 * HOUR),
          event_text: null,
          place: 'DHL Hub บางนา',
          checked_at: iso(3 * MIN),
          label_at: iso(44 * HOUR),
          track_url: 'https://www.dhl.com/th-th/home/tracking/tracking-ecommerce.html?submit=1&tracking-id=7127099912870601',
          scans: [
            scanAt('77169', 'IN_TRANSIT', 'พัสดุออกจากศูนย์คัดแยก', 'DHL Hub บางนา', 31 * HOUR),
            scanAt('77206', 'PICKED_UP', 'เข้ารับพัสดุแล้ว', null, 40 * HOUR),
          ],
          weight_g: 4100,
          quiet_h: 31,
          epod_name: null,
          epod_signature: null,
          delivery_image: null,
        },
      },
      {
        ...BASE,
        id: '292377',
        code: '20260925-061',
        carrier: 'dhl',
        phase: 'in_transit',
        delivery_date: bkkDay(-1),
        pack: 'ทดลองทาน 9 แพ็ค',
        meals: 9,
        total: 640,
        payment: { method: 'CashOnDelivery', cod: true },
        updated_at: iso(95 * MIN),
        steps_at: [iso(20 * HOUR), iso(17 * HOUR), null, null],
        dhl: {
          tracking_no: '7127099912870646',
          status_key: 'IN_TRANSIT',
          status_code: '77178',
          status_text: 'ถึงศูนย์กระจายพัสดุปลายทาง',
          event_at: iso(95 * MIN),
          event_text: null,
          place: 'DHL ศูนย์เชียงใหม่',
          checked_at: iso(3 * MIN),
          label_at: iso(20 * HOUR),
          track_url: 'https://www.dhl.com/th-th/home/tracking/tracking-ecommerce.html?submit=1&tracking-id=7127099912870646',
          scans: [
            scanAt('77178', 'IN_TRANSIT', 'ถึงศูนย์กระจายพัสดุปลายทาง', 'DHL ศูนย์เชียงใหม่', 95 * MIN),
            scanAt('77169', 'IN_TRANSIT', 'พัสดุออกจากศูนย์คัดแยก', 'DHL Hub บางนา', 8 * HOUR),
            scanAt('77013', 'IN_TRANSIT', 'พัสดุถึงศูนย์คัดแยก', 'DHL Hub บางนา', 12 * HOUR),
            scanAt('77206', 'PICKED_UP', 'เข้ารับพัสดุแล้ว', null, 17 * HOUR),
            scanAt('77123', 'SUBMITTED', 'ข้อมูลพัสดุรับเข้าระบบ', null, 20 * HOUR),
          ],
          weight_g: 3200,
          quiet_h: null,
          epod_name: null,
          epod_signature: null,
          delivery_image: null,
        },
      },
      {
        ...BASE,
        id: '292501',
        code: '20260926-118',
        carrier: 'lalamove',
        phase: 'on_the_way',
        delivery_date: today,
        slot: onTime.label,
        round: { no: 2, of: 4 },
        pack: 'Pro 7 day 21 มื้อ',
        updated_at: iso(2 * MIN),
        timing: { slot_end: onTime.end, late_min: 0, basis: 'eta' },
        steps_at: [null, iso(34 * MIN), null, null],
        lalamove: {
          status: 'PICKED_UP',
          matched: 'stop',
          driver: {
            name: 'สมชาย ใจดี',
            plate: '1กข 2345',
            phone: '0811112222',
            phone_display: '081-111-2222',
            photo: DRIVER_A,
          },
          stops_ahead: 1,
          stops_total: 4,
          stops_done: 2,
          picked_up_at: iso(34 * MIN),
          delivered_at: null,
          location: { at: iso(2 * MIN), age_min: 2, fresh: true },
          eta: { minutes: 18, arrival: bkkClock(18 * MIN) },
          still_min: null,
          distance_km: 4.2,
          share_link: 'https://share.lalamove.com/?TH-demo',
          pod_image: null,
        },
      },
      {
        ...BASE,
        id: '292544',
        code: '20260926-131',
        carrier: 'lalamove',
        phase: 'on_the_way',
        delivery_date: today,
        slot: late.label,
        pack: 'Lean 5 day 10 มื้อ',
        meals: 10,
        total: 890,
        updated_at: iso(1 * MIN),
        timing: { slot_end: late.end, late_min: 59, basis: 'eta' },
        steps_at: [null, iso(27 * MIN), null, null],
        lalamove: {
          status: 'PICKED_UP',
          matched: 'stop',
          driver: {
            name: 'วิชัย รักดี',
            plate: '2ขค 8899',
            phone: '0823334444',
            phone_display: '082-333-4444',
            photo: null,
          },
          stops_ahead: 3,
          stops_total: 5,
          stops_done: 1,
          picked_up_at: iso(27 * MIN),
          delivered_at: null,
          location: { at: iso(1 * MIN), age_min: 1, fresh: true },
          eta: { minutes: late.etaMin, arrival: bkkClock(late.etaMin * MIN) },
          still_min: 12,
          distance_km: null,
          share_link: 'https://share.lalamove.com/?TH-demo2',
          pod_image: null,
        },
      },
      {
        ...BASE,
        id: '292145',
        code: '20260923-044',
        carrier: 'dhl',
        phase: 'delivered',
        delivery_date: bkkDay(-2),
        pack: 'Pro 14 day 42 มื้อ',
        meals: 42,
        total: 2450,
        pos_status: '4',
        pos_status_text: 'จัดส่งเสร็จสิ้น',
        updated_at: iso(22 * HOUR),
        delivered_at: iso(22 * HOUR),
        dim: true,
        steps_at: [iso(46 * HOUR), iso(43 * HOUR), iso(27 * HOUR), iso(22 * HOUR)],
        dhl: {
          tracking_no: '7127099912870522',
          status_key: 'DELIVERED',
          status_code: '77093',
          status_text: 'จัดส่งเรียบร้อย',
          event_at: iso(22 * HOUR),
          event_text: null,
          place: null,
          checked_at: iso(3 * MIN),
          label_at: iso(46 * HOUR),
          track_url: 'https://www.dhl.com/th-th/home/tracking/tracking-ecommerce.html?submit=1&tracking-id=7127099912870522',
          scans: [
            scanAt('77093', 'DELIVERED', 'จัดส่งเรียบร้อย', 'เชียงราย', 22 * HOUR),
            scanAt('77090', 'OUT_FOR_DELIVERY', 'อยู่ระหว่างนำส่ง', 'เชียงราย', 27 * HOUR),
            scanAt('77206', 'PICKED_UP', 'เข้ารับพัสดุแล้ว', null, 43 * HOUR),
          ],
          weight_g: 6800,
          quiet_h: null,
          epod_name: 'คุณสมศรี (เจ้าตัว)',
          epod_signature: SIGNATURE,
          delivery_image: POD_DHL,
        },
      },
      {
        ...BASE,
        id: '292440',
        code: '20260926-102',
        carrier: 'lalamove',
        phase: 'delivered',
        delivery_date: today,
        slot: doneRound.label,
        pos_status: '4',
        pos_status_text: 'จัดส่งเสร็จสิ้น',
        updated_at: iso(38 * MIN),
        delivered_at: iso(38 * MIN),
        dim: true,
        timing: { slot_end: doneRound.end, late_min: 0, basis: 'delivered' },
        steps_at: [null, iso(95 * MIN), null, iso(38 * MIN)],
        lalamove: {
          status: 'COMPLETED',
          matched: 'stop',
          driver: { name: 'มานพ ส่งไว', plate: '3กค 1122', phone: '0845556666', phone_display: '084-555-6666', photo: DRIVER_B },
          stops_ahead: 0,
          stops_total: 2,
          stops_done: 2,
          picked_up_at: iso(95 * MIN),
          delivered_at: iso(38 * MIN),
          location: null,
          eta: null,
          still_min: null,
          distance_km: null,
          share_link: null,
          pod_image: POD_LALA,
        },
      },
    ]
    return { state: 'ok', orders, more: 0, today, fetched_at: new Date(now - 20_000).toISOString(), stale: false }
  }
  if (conversationId === 'c104') {
    return {
      state: 'ok',
      today,
      fetched_at: new Date(now - 5_000).toISOString(),
      stale: false,
      more: 0,
      orders: [
        {
          ...BASE,
          id: '292544',
          code: '20260926-140',
          carrier: 'lalamove',
          phase: 'to_pickup',
          delivery_date: today,
          slot: '14.00-16.00 น.',
          pos_status: '2',
          pos_status_text: 'กำลังจัดเตรียมอาหาร',
          updated_at: iso(6 * MIN),
          steps_at: [null, null, null, null],
          lalamove: {
            status: 'ON_GOING',
            matched: 'stop',
            driver: { name: 'วิชัย รักดี', plate: '2ขค 8899', phone: '0823334444', phone_display: '082-333-4444', photo: null },
            stops_ahead: null,
            stops_total: 3,
            stops_done: 0,
            picked_up_at: null,
            delivered_at: null,
            location: { at: iso(21 * MIN), age_min: 21, fresh: false },
            eta: null,
            share_link: 'https://share.lalamove.com/?TH-demo3',
          },
        },
        {
          ...BASE,
          id: '292610',
          code: '20260926-150',
          carrier: 'dhl',
          phase: 'label_created',
          delivery_date: today,
          pos_status: '2',
          pos_status_text: 'กำลังจัดเตรียมอาหาร',
          updated_at: iso(9 * HOUR),
          steps_at: [iso(9 * HOUR), null, null, null],
          dhl: {
            tracking_no: '7127099912871102',
            status_key: 'SUBMITTED',
            status_code: '77123',
            status_text: 'ข้อมูลพัสดุรับเข้าระบบ',
            event_at: iso(9 * HOUR),
            event_text: null,
            place: null,
            checked_at: iso(3 * MIN),
            label_at: iso(9 * HOUR),
            track_url: 'https://www.dhl.com/th-th/home/tracking/tracking-ecommerce.html?submit=1&tracking-id=7127099912871102',
            scans: [scanAt('77123', 'SUBMITTED', 'ข้อมูลพัสดุรับเข้าระบบ', null, 9 * HOUR)],
            weight_g: 2900,
          },
        },
        {
          ...BASE,
          id: '292702',
          code: '20260926-166',
          carrier: 'lalamove',
          phase: 'scheduled',
          delivery_date: bkkDay(2),
          slot: '11.00-13.00 น.',
          round: { no: 1, of: 2 },
          pos_status: '1',
          pos_status_text: 'ชำระเงินแล้ว',
        },
      ],
    }
  }
  if (conversationId === 'c107') {
    return { state: 'ok', orders: [], more: 0, today, fetched_at: new Date(now - 5_000).toISOString(), stale: false }
  }
  return { state: 'no_customer', orders: [], more: 0 }
}

class MockDeliveriesApi implements DeliveriesApi {
  async get(conversationId: string): Promise<Deliveries> {
    await new Promise((r) => setTimeout(r, 120))
    return mockDeliveries(conversationId)
  }
}

let current: DeliveriesApi | null = null

export function getDeliveriesApi(): DeliveriesApi {
  if (!current) {
    const base = (import.meta.env.VITE_API_BASE ?? '').trim()
    current = useMock ? new MockDeliveriesApi() : new HttpDeliveriesApi(base)
  }
  return current
}

/** Tests inject their own backend. */
export function setDeliveriesApi(api: DeliveriesApi | null): void {
  current = api
}
